import {
  ErhlCustomerApplicationProcessSteps,
  ErhlCustomerProcessStep,
  ErhlCustomerProcessStepNames,
  StepStatus,
  type ErhlCustomerProcessStatus,
  type ErhlCustomerProcessStepEnum,
  type LoanApplication,
} from '@/types';
import { assign, createMachine } from 'xstate';
import { createLoanApp } from './builder';
import { ErhlEquityAccessMethod, LoanType } from './const';

/**
 * State machine for the customer-facing (D2C) ERHL application journey.
 *
 * Duplicated from `loanErhlAppMachine` (the broker ERHL journey) rather than branched from it,
 * so the two journeys can diverge independently. There is no broker on this journey: the
 * applicant is always the primary applicant creating a new application, verifies their mobile
 * number before submitting, and always completes every ERHL step.
 */
export interface ErhlCustomerLoanAppMachineContext {
  loanApp: LoanApplication;
  from?: string;
  authorisedUser: Record<string, string>;
  status: ErhlCustomerProcessStatus;
  current: ErhlCustomerProcessStepEnum;
}

export const initMachine = (): ErhlCustomerLoanAppMachineContext => ({
  loanApp: {
    ...createLoanApp({ userType: 'Primary', actionType: 'createNew' }),
    lendingProduct: 'erhl',
    loanType: LoanType.EQUITY_RELEASE_HOME_LOAN,
    erhlEquityAccessMethod: ErhlEquityAccessMethod.LUMP_SUM_AND_MONTHLY_DRAWDOWN,
  },
  authorisedUser: {},
  status: Object.fromEntries(
    ErhlCustomerProcessStepNames.map((step) => [
      step,
      step === ErhlCustomerProcessStep.LoanPurpose ? StepStatus.Ready : StepStatus.Incomplete,
    ]),
  ) as ErhlCustomerProcessStatus,
  current: ErhlCustomerProcessStep.LoanPurpose,
});

export type ErhlCustomerLoanAppMachineEvent =
  | { type: 'NEXT'; loanApp: LoanApplication }
  | { type: 'UPDATE'; loanApp: LoanApplication }
  | { type: 'BACK' }
  | { type: 'READY' }
  | { type: 'GOTO'; step: ErhlCustomerProcessStepEnum }
  | { type: 'RELOAD'; state: ErhlCustomerLoanAppMachineContext }
  | { type: 'RESTART' };

/**
 * Whether the journey may jump straight to `step`: only a step that is Ready or already Completed.
 * Shared by the machine's `GOTO` guard and the layout's route watcher so the two never disagree.
 */
export const canGotoStep = (status: ErhlCustomerProcessStatus, step: ErhlCustomerProcessStepEnum) =>
  status[step] === StepStatus.Ready || status[step] === StepStatus.Completed;

/** Builds a `NEXT` transition to `target` that records the step's data */
const next = (target: ErhlCustomerProcessStepEnum) => ({
  on: {
    NEXT: {
      target,
      actions: 'saveData',
    },
  },
});

export const loanApplicationMachineErhlCustomer = createMachine<
  ErhlCustomerLoanAppMachineContext,
  ErhlCustomerLoanAppMachineEvent
>(
  {
    id: 'loanApplicationErhlCustomer',
    predictableActionArguments: true,
    schema: {
      context: {} as ErhlCustomerLoanAppMachineContext,
    },
    context: initMachine(),
    initial: 'start',
    states: {
      start: {
        on: {
          NEXT: {
            target: 'gotoReady',
          },
        },
      },
      [ErhlCustomerProcessStep.LoanPurpose]: next(ErhlCustomerProcessStep.PropertyDetails),
      [ErhlCustomerProcessStep.PropertyDetails]: next(ErhlCustomerProcessStep.ApplicantDetails),
      [ErhlCustomerProcessStep.ApplicantDetails]: next(ErhlCustomerProcessStep.LoanDetails),
      [ErhlCustomerProcessStep.LoanDetails]: next(ErhlCustomerProcessStep.PropertyOwnership),
      [ErhlCustomerProcessStep.PropertyOwnership]: next(ErhlCustomerProcessStep.Income),
      [ErhlCustomerProcessStep.Income]: next(ErhlCustomerProcessStep.Expenses),
      [ErhlCustomerProcessStep.Expenses]: next(ErhlCustomerProcessStep.Assets),
      [ErhlCustomerProcessStep.Assets]: next(ErhlCustomerProcessStep.ExitStrategy),
      [ErhlCustomerProcessStep.ExitStrategy]: next(ErhlCustomerProcessStep.VerifyIdentity),
      [ErhlCustomerProcessStep.VerifyIdentity]: next(ErhlCustomerProcessStep.SubmitApplication),
      [ErhlCustomerProcessStep.SubmitApplication]: next(ErhlCustomerProcessStep.Finished),
      [ErhlCustomerProcessStep.Finished]: {
        on: {
          NEXT: {
            target: ErhlCustomerProcessStep.LoanPurpose,
            actions: 'resetLoanApplication',
          },
        },
      },
      prevStage: {
        always: [...ErhlCustomerProcessStepNames]
          .sort((a, b) => ErhlCustomerApplicationProcessSteps[b]?.order - ErhlCustomerApplicationProcessSteps[a]?.order)
          .map((step) => ({
            target: step,
            cond: (ctx) =>
              ErhlCustomerApplicationProcessSteps[ctx.current].order >
                ErhlCustomerApplicationProcessSteps[step].order && ctx.status[step] === StepStatus.Completed,
          })),
      },
      gotoReady: {
        always: ErhlCustomerProcessStepNames.map((step) => ({
          target: step,
          cond: (ctx) => ctx.status[step] === StepStatus.Ready,
        })),
      },
    },
    on: {
      BACK: {
        target: 'prevStage',
        cond: (ctx) => ctx.current !== ErhlCustomerProcessStep.LoanPurpose,
      },
      READY: {
        target: 'gotoReady',
      },
      UPDATE: {
        actions: 'updateData',
      },
      GOTO: ErhlCustomerProcessStepNames.map((step) => ({
        target: step,
        cond: (ctx, evt) => evt.step === step && canGotoStep(ctx.status, step),
      })),
      RELOAD: {
        target: 'gotoReady',
        actions: 'updateContext',
      },
      RESTART: {
        target: ErhlCustomerProcessStep.LoanPurpose,
        actions: 'resetLoanApplication',
      },
    },
  },
  {
    actions: {
      // An in-step save: records the data but leaves step statuses alone, so the current step stays
      // Ready and a reload (RELOAD -> gotoReady) can still find it.
      updateData: assign((context, event) => (event.type === 'UPDATE' ? { loanApp: event.loanApp } : {})),
      saveData: assign((context, event) => {
        if (event.type !== 'NEXT') {
          return {};
        }

        const currentStep = ErhlCustomerApplicationProcessSteps[context.current];
        if (!currentStep) {
          return { loanApp: event.loanApp };
        }

        const status = Object.fromEntries(
          Object.entries(context.status)
            .filter(([step]) => ErhlCustomerApplicationProcessSteps[step as ErhlCustomerProcessStepEnum])
            .map(([step, status]) => [
              step,
              step === context.current
                ? StepStatus.Completed
                : ErhlCustomerApplicationProcessSteps[step as ErhlCustomerProcessStepEnum].order < currentStep.order
                ? status
                : StepStatus.Incomplete,
            ]),
        ) as ErhlCustomerProcessStatus;

        return { loanApp: event.loanApp, status };
      }),
      updateContext: assign((context, event) => (event.type === 'RELOAD' ? event.state : {})),
      resetLoanApplication: assign(() => initMachine()),
    },
  },
);
